import React, { useEffect, useRef } from 'react';
import { Dialog } from '@/shared/components/Dialog';
import { Textarea } from '@/shared/components/TextField';
import { Snackbar } from '@/shared/components/Snackbar';
import FileUpload from '@/shared/components/FileUpload/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';
import {
  COMPLETE_CR_MAX_FILES,
  COMPLETE_CR_MAX_FILE_SIZE_MB,
  COMPLETE_CR_NOTES_MAX_LENGTH,
} from '../../hooks/useCompleteContactRequest';
import { StatusBadge } from './viewContactRequestUI';

/** File types an admin may attach when completing a request. */
const ALLOWED_FILE_TYPES = [
  FileType.JPG,
  FileType.JPEG,
  FileType.PNG,
  FileType.PDF,
  FileType.DOCX,
];

interface CompleteContactRequestModalProps {
  open: boolean;
  onClose: () => void;
  currentStatus: string;

  // File selection (uploaded to S3 immediately on selection)
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** True while a selected file is still uploading — blocks submit. */
  isUploading: boolean;

  // Toast (file-validation / notes-or-attachment rule / upload failures)
  toastMessage: string;
  onToast: (message: string) => void;
  onClearToast: () => void;

  // Completion note
  notes: string;
  onNotesChange: (value: string) => void;
  notesError: string;

  // Submit
  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
}

const InfoIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#474A4C" strokeWidth="1.5" />
    <line
      x1="12"
      y1="11"
      x2="12"
      y2="16.5"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="12" cy="8" r="1" fill="#474A4C" />
  </svg>
);

/**
 * A read-only status field box (label floated above a status badge). The
 * `dimmed` variant fades the whole field (used for the inactive "current
 * status"); the default keeps the badge at full colour (used for the "new
 * status" target).
 */
const StatusField: React.FC<{
  label: string;
  testId: string;
  children: React.ReactNode;
  dimmed?: boolean;
}> = ({ label, testId, children, dimmed = false }) => (
  <div
    className={`relative shrink-0 rounded-lg bg-[#E5E7E9] cursor-not-allowed px-3 w-[318px] max-w-full${
      dimmed ? ' opacity-60' : ''
    }`}
    style={{ minHeight: 56 }}
    data-testid={testId}
    aria-disabled="true"
  >
    <span
      className="absolute left-3 pointer-events-none"
      style={{
        top: 0,
        paddingTop: 8,
        fontSize: 12,
        lineHeight: '16px',
        color: '#6a7278',
      }}
    >
      {label}
    </span>
    <div className="pt-7 pb-2">{children}</div>
    <div
      className="absolute bottom-0"
      style={{ left: 6, right: 6, height: 1, backgroundColor: '#6a7278' }}
    />
  </div>
);

/**
 * "Complete & send response" modal for the assigned admin (RR-1097). Mirrors the
 * service-request completion modal: read-only current/new status fields, an
 * irreversible-action banner, a file uploader (max 5, ≤5 MB each), and a note
 * textarea. The new status is fixed to "Zakończone" (completed).
 */
const CompleteContactRequestModal: React.FC<
  CompleteContactRequestModalProps
> = ({
  open,
  onClose,
  currentStatus,
  files,
  onFilesChange,
  isUploading,
  toastMessage,
  onToast,
  onClearToast,
  notes,
  onNotesChange,
  notesError,
  isSubmitting,
  submitError,
  onSubmit,
}) => {
  // Marker at the end of the upload area. When a new attachment is added the
  // file list grows below the (tall) drop zone and can fall outside the
  // modal's scroll viewport — scroll it into view so the user sees the file
  // they just uploaded. Only react to growth (uploads), not removals.
  const uploadEndRef = useRef<HTMLDivElement>(null);
  const prevFileCountRef = useRef(files.length);
  useEffect(() => {
    if (
      files.length > prevFileCountRef.current &&
      typeof uploadEndRef.current?.scrollIntoView === 'function'
    ) {
      uploadEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
    prevFileCountRef.current = files.length;
  }, [files.length]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      data-testid="complete-cr-modal"
    >
      <div className="flex flex-col relative">
        <div className="px-6 pt-6 pb-2">
          <h2
            className="text-neutral-10 font-normal"
            style={{ fontSize: 22, lineHeight: '28px' }}
            data-testid="complete-cr-title"
          >
            {L.COMPLETE_CR_TITLE}
          </h2>
        </div>

        <div className="px-6 pt-4 flex flex-col gap-8 max-h-[60vh] overflow-y-auto">
          <StatusField
            label={L.COMPLETE_CR_FIELD_CURRENT_STATUS}
            testId="complete-cr-current-status-field"
            dimmed
          >
            <StatusBadge status={currentStatus} />
          </StatusField>

          <div className="flex flex-col gap-2">
            <StatusField
              label={L.COMPLETE_CR_FIELD_NEW_STATUS}
              testId="complete-cr-new-status-field"
            >
              <StatusBadge status="completed" />
            </StatusField>

            <div
              className="flex items-start gap-2"
              data-testid="complete-cr-irreversible-banner"
            >
              <InfoIcon />
              <p style={{ fontSize: 14, lineHeight: '20px', color: '#474A4C' }}>
                <span className="font-semibold">
                  {L.COMPLETE_CR_BANNER_BOLD}
                </span>
                {L.COMPLETE_CR_BANNER_REST}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-sm leading-5 tracking-[0.25px] text-start text-[#474A4C] font-normal text-[18px]">
              {L.COMPLETE_CR_SECTION_UPLOAD}
            </p>
            <FileUpload
              onFileSelect={onFilesChange}
              selectedFiles={files}
              allowedTypes={ALLOWED_FILE_TYPES}
              maxFiles={COMPLETE_CR_MAX_FILES}
              maxFileSizeMb={COMPLETE_CR_MAX_FILE_SIZE_MB}
              onError={onToast}
              disabled={isSubmitting}
              fullWidth
            />
            <div ref={uploadEndRef} aria-hidden="true" />
          </div>

          <div data-field="complete-cr-notes">
            <Textarea
              label={L.COMPLETE_CR_FIELD_NOTES}
              placeholder={L.COMPLETE_CR_FIELD_NOTES}
              value={notes}
              onChange={(e) =>
                onNotesChange((e.target as HTMLTextAreaElement).value ?? '')
              }
              maxLength={COMPLETE_CR_NOTES_MAX_LENGTH}
              rows={4}
              fullWidth
              error={Boolean(notesError)}
              errorMessage={notesError}
              disabled={isSubmitting}
              dataTestId="complete-cr-notes-input"
            />
          </div>

          {submitError && (
            <p
              className="text-error-40"
              style={{ fontSize: 12, lineHeight: '16px' }}
              data-testid="complete-cr-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="complete-cr-cancel"
          >
            {L.COMPLETE_CR_BUTTON_CANCEL}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting || isUploading}
            className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting || isUploading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#1f3340] cursor-pointer'
            }`}
            data-testid="complete-cr-submit"
          >
            {isSubmitting
              ? L.COMPLETE_CR_BUTTON_SUBMITTING
              : L.COMPLETE_CR_BUTTON_SUBMIT}
          </button>
        </div>
      </div>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        variant="error"
        onClose={onClearToast}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
    </Dialog>
  );
};

export default CompleteContactRequestModal;
